/**
 * About.jsx — the firm's story, timeline and geographic reach.
 */
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const timeline = [
    { year: "2009",        title: "The beginning",   text: "Tax Solution founded on 25 May 2009 at Banipur Tiniali, Dibrugarh — a single desk, one mission." },
    { year: "2011 – 2015", title: "Roots take hold", text: "Serving the public and government offices of Assam. Learning under fire — every case a study, every study a strength." },
    { year: "2016 – 2020", title: "Regional reach", text: "Practice extends across Arunachal Pradesh and Meghalaya. Handling institutional accounts and complex compliance." },
    { year: "2020 – 2024", title: "Crossing borders", text: "First NRI mandates from Japan, Oman and Germany. Cross-border advisory becomes a specialty." },
    { year: "2025",        title: "Ghatoshkash",      text: "On 28 November 2025, the practice incorporates as Ghatoshkash Tax Wizards Pvt. Ltd. — carrying forward the Tax Solution India name." },
];

const regions = [
    { label: "Assam",              country: "India", primary: true },
    { label: "Arunachal Pradesh",  country: "India", primary: true },
    { label: "Meghalaya",          country: "India", primary: true },
    { label: "Japan",              country: "NRI",   primary: false },
    { label: "Oman",               country: "NRI",   primary: false },
    { label: "Germany",            country: "NRI",   primary: false },
];

export default function About() {
    return (
        <div className="font-outfit" data-testid="marketing-about">
            {/* ─── HERO ─── */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20">
                <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-5">
                    Our story · seventeen years in
                </div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-[5rem] font-light tracking-tighter leading-[1.02] text-[#0A1128] max-w-4xl">
                    Tax Solution India — <span className="italic">an ally,</span> steady since 2009.
                </h1>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-14">
                    <div className="lg:col-span-7 space-y-6 text-lg font-light leading-relaxed text-[#0A1128]/75">
                        <p>
                            The name <b className="font-medium text-[#0A1128]">Tax Solution</b> first arose on
                            25 May 2009 at Banipur Tiniali, Dibrugarh — with a clear mission to serve both
                            the government and the public in matters of taxation and accounting.
                        </p>
                        <p>
                            From the very beginning the journey saw ups and downs. Yet, with the support
                            of countless hands and minds, the entity grew stronger, more reliable — truly resilient.
                            Each challenge became a case study; each case added to our experience.
                        </p>
                        <p>
                            Over the years we&apos;ve proudly served government and non-government entities across
                            Assam, Arunachal Pradesh and Meghalaya. Our expertise then reached across borders —
                            handling Non-Resident Indian clients from Japan, Oman and Germany.
                        </p>
                        <p>
                            After nearly seventeen years, on 28 November 2025, Tax Solution transformed from
                            a sole proprietorship into a registered company — <span className="italic">Ghatoshkash Tax Wizards Pvt. Ltd.</span>
                            — carrying forward the project name Tax Solution India. Registration is a matter of legal documentation;
                            the emotions and dedication behind this milestone are far greater.
                        </p>
                    </div>

                    <div className="lg:col-span-5 lg:pt-6">
                        <div className="bg-[#FDFBF7] border border-[#0A1128]/10 p-8">
                            <Sparkles size={22} className="text-[#C5A059]" strokeWidth={1.4} />
                            <div className="mt-4 text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059]">
                                Meaning of our corporate name
                            </div>
                            <div className="font-serif text-2xl leading-snug mt-2 text-[#0A1128]">
                                &ldquo;Ghatoshkash&rdquo; — the spirit of a trusted ally.
                            </div>
                            <p className="text-sm font-light leading-relaxed text-[#0A1128]/75 mt-4">
                                Our parent company, Ghatoshkash Tax Wizards Pvt. Ltd., is named after
                                the legendary figure who helped the Pandavas triumph in the war of good.
                                We aspire to be that quiet, dependable ally in the victories of our clients —
                                under the Tax Solution India banner.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── TIMELINE ─── */}
            <section className="bg-[#FDFBF7] border-y border-[#0A1128]/10 py-24 md:py-32">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-4">
                        A seventeen-year journey
                    </div>
                    <h2 className="font-serif text-4xl lg:text-5xl font-normal tracking-tight leading-tight text-[#0A1128]">
                        Milestones along the way.
                    </h2>

                    <ol className="mt-16 relative border-l-2 border-[#0A1128]" data-testid="about-timeline">
                        {timeline.map((t, i) => (
                            <motion.li
                                key={t.year}
                                className="pl-10 pb-14 relative"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
                            >
                                <span className="absolute -left-[9px] top-1.5 w-4 h-4 bg-[#C5A059] border-2 border-[#0A1128]" />
                                <div className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C5A059]">
                                    {t.year}
                                </div>
                                <div className="font-serif text-2xl lg:text-3xl font-medium tracking-tight text-[#0A1128] mt-1">
                                    {t.title}
                                </div>
                                <p className="text-base font-light leading-relaxed text-[#0A1128]/75 mt-3 max-w-2xl">
                                    {t.text}
                                </p>
                            </motion.li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* ─── REACH ─── */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-3">
                            Our reach
                        </div>
                        <h2 className="font-serif text-4xl lg:text-5xl font-normal tracking-tight leading-tight text-[#0A1128]">
                            From Dibrugarh<br />
                            <span className="italic">to the world.</span>
                        </h2>
                        <p className="mt-6 text-lg font-light leading-relaxed text-[#0A1128]/75 max-w-md">
                            Deep local roots in India&apos;s north-east — and a growing family of NRI
                            clients whose lives take them further afield.
                        </p>
                    </div>

                    <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {regions.map((r) => (
                            <div
                                key={r.label}
                                data-testid={`reach-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`p-6 border transition-all duration-300 hover:-translate-y-1 ${
                                    r.primary
                                        ? "bg-[#0A1128] text-[#FAF3E0] border-[#0A1128]"
                                        : "bg-[#FDFBF7] text-[#0A1128] border-[#0A1128]/10 hover:border-[#C5A059]"
                                }`}
                            >
                                <div className={`w-2.5 h-2.5 ${r.primary ? "bg-[#C5A059]" : "bg-[#C5A059]"}`} />
                                <div className="font-serif text-2xl font-medium tracking-tight mt-4">{r.label}</div>
                                <div className={`text-[10px] uppercase tracking-[0.25em] mt-2 font-semibold ${r.primary ? "text-[#C5A059]" : "text-[#C5A059]"}`}>
                                    {r.country}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
